/**
 * ThemeSummary Component
 * Visar sammanfattning när alla frågor i ett tema är besvarade
 * Inkluderar median score, elevated score och tolkning
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import lifeThemeAPI from '../../services/lifeThemeAPI';
import Icon from '../Icon';

const ThemeSummary = () => {
  const { themeId } = useParams();
  const navigate = useNavigate();
  
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [explorationProgress, setExplorationProgress] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(true);

  useEffect(() => {
    loadSummary();
    loadExplorationProgress();
  }, [themeId]);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const data = await lifeThemeAPI.getThemeSummary(themeId);
      setSummary(data);
    } catch (err) {
      console.error('Error loading summary:', err);
      setError('Kunde inte ladda sammanfattning. Alla frågor måste besvaras först.');
    } finally {
      setLoading(false);
    }
  };

  const loadExplorationProgress = async () => {
    try {
      setLoadingProgress(true);
      const data = await lifeThemeAPI.getExplorationProgress(themeId);
      console.log('🔍 Exploration Progress Data:', data);
      setExplorationProgress(data);
    } catch (err) {
      console.error('Error loading exploration progress:', err);
      // Don't show error - it's fine if exploration hasn't started yet
      setExplorationProgress(null);
    } finally {
      setLoadingProgress(false);
    }
  };

  const handleResetAnswers = async () => {
    if (!window.confirm(`Är du säker på att du vill radera alla svar för "${summary.theme_name}" och börja om? Detta kan inte ångras.`)) {
      return;
    }

    try {
      setResetting(true);
      await lifeThemeAPI.resetThemeAnswers(themeId);
      
      // Visa bekräftelse och navigera till första frågan
      alert(`Alla svar för "${summary.theme_name}" har raderats. Du kan nu svara om på frågorna.`);
      navigate(`/client/life-themes/${themeId}/progress`);
    } catch (err) {
      console.error('Error resetting answers:', err);
      alert('Kunde inte radera svaren. Försök igen senare.');
    } finally {
      setResetting(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 5) return 'text-red-600';
    if (score >= 4) return 'text-orange-600';
    if (score >= 3) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getScoreBgColor = (score) => {
    if (score >= 5) return 'bg-red-100';
    if (score >= 4) return 'bg-orange-100';
    if (score >= 3) return 'bg-yellow-100';
    return 'bg-green-100';
  };

  const getElevationLevel = (elevatedScore) => {
    if (elevatedScore >= 50) {
      return {
        level: 'Mycket starkt aktivt',
        color: 'red',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-900',
        description: 'Detta livstema har mycket stor påverkan på ditt välbefinnande. Vi rekommenderar starkt att du utforskar detta tema djupare med hjälp av vårt AI-stöd eller psykoterapi.',
        actionable: true
      };
    }
    if (elevatedScore >= 30) {
      return {
        level: 'Starkt aktivt',
        color: 'orange',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        textColor: 'text-orange-900',
        description: 'Detta livstema påverkar ditt välbefinnande i betydande grad. Det kan vara värdefullt att utforska detta tema vidare med AI-stöd eller psykoterapi..',
        actionable: true
      };
    }
    if (elevatedScore >= 15) {
      return {
        level: 'Måttligt aktivt',
        color: 'yellow',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        textColor: 'text-yellow-900',
        description: 'Detta livstema har viss påverkan på ditt välbefinnande. Du kan välja att utforska det vidare eller fokusera på andra teman med högre aktivering.',
        actionable: false
      };
    }
    return {
      level: 'Låg aktivering',
      color: 'green',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-900',
      description: 'Detta livstema har låg påverkan på ditt välbefinnande just nu. Vi rekommenderar att du fokuserar på andra teman som visar högre aktivering.',
      actionable: false
    };
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <p className="text-yellow-800 mb-4">{error || 'Sammanfattning kunde inte laddas'}</p>
          <button
            onClick={() => navigate(`/client/life-themes/${themeId}/progress`)}
            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
          >
            Fortsätt besvara frågor
          </button>
        </div>
      </div>
    );
  }

  const elevationAnalysis = getElevationLevel(summary.elevated_score);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Theme Name - Top */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {summary.theme_name}
        </h1>
        <p className="text-gray-600">
          <Icon name="check-circle" size={16} className="inline mr-2" /> <span>Utvärdering slutförd • {summary.total_questions} frågor besvarade</span>
        </p>
      </div>

      {/* Theme Description Card */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 shadow mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Om detta livstema
        </h3>
        <p className="text-gray-700 leading-relaxed">
          {summary.theme_description}
        </p>
      </div>

      {/* Elevation Impact Card - White with shadow */}
      <div className="bg-white shadow-lg rounded-lg p-8 mb-6">
        
        {/* Interpretation - Top of box */}
        <div className="mb-6 pb-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">
            Klinisk tolkning
          </h3>
          <p className="text-gray-700 leading-relaxed">
            {summary.interpretation}
          </p>
          {/* AI Support CTA - Inside box */}
            {elevationAnalysis.actionable && (
            <button
                onClick={() => navigate('/client/chat', { 
                  state: { 
                    lifeThemeContext: {
                      themeId: parseInt(themeId),
                      themeName: summary.theme_name,
                      themeDescription: summary.theme_description,
                      medianScore: summary.median_score,
                      elevatedScore: summary.elevated_score,
                      totalQuestions: summary.total_questions,
                      elevationLevel: elevationAnalysis.level
                    }
                  }
                })}
                className="w-full mt-4 py-4 px-6 bg-brand-primary text-white rounded-lg font-semibold hover:bg-brand-primary-dark transition-colors shadow-md hover:shadow-lg"
            >
                <div className="flex items-center justify-center gap-3">
                <Icon name="bot" size={24} className="text-white" />
                <span>
                  {explorationProgress && explorationProgress.total_messages > 0 
                    ? 'Fortsätt AI-utforskning' 
                    : 'Utforska med AI'}
                </span>
                </div>
            </button>
            )}
        </div>

        {/* Elevation Level */}
        <div className="mb-6">
          <div className="flex items-start gap-4 w-full">
            <div className={`flex-shrink-0 w-3 h-3 rounded-full mt-2 ${
              elevationAnalysis.color === 'red' ? 'bg-red-500' :
              elevationAnalysis.color === 'orange' ? 'bg-orange-500' :
              elevationAnalysis.color === 'yellow' ? 'bg-yellow-500' :
              'bg-green-500'
            }`}></div>
            <div className="flex-1">
              <h2 className={`text-xl font-bold mb-2 ${
                elevationAnalysis.color === 'red' ? 'text-red-900' :
                elevationAnalysis.color === 'orange' ? 'text-orange-900' :
                elevationAnalysis.color === 'yellow' ? 'text-yellow-900' :
                'text-green-900'
              }`}>
                {elevationAnalysis.level}
              </h2>
              <p className="text-gray-700 leading-relaxed">
                {elevationAnalysis.description}
              </p>
              {/* Reset Button - Small, inside box */}
            <div className="mt-1">
                <button
                    onClick={handleResetAnswers}
                    disabled={resetting}
                    className="text-sm px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {resetting ? 'Raderar svar...' : 'Svara om på frågorna'}
                </button>
            </div>
            </div>
          </div>
          
        </div>

        {/* Progress Bars - Using QuestionDetail style */}
        <div className="space-y-4 mb-6">
          {/* Elevated Score Progress Bar */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Höga svar (5-6)
              </span>
              <span className="text-sm text-gray-600">
                {summary.elevated_score.toFixed(0)}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-500 ${
                  elevationAnalysis.color === 'red' ? 'bg-red-500' :
                  elevationAnalysis.color === 'orange' ? 'bg-orange-500' :
                  elevationAnalysis.color === 'yellow' ? 'bg-yellow-500' :
                  'bg-green-500'
                }`}
                style={{ width: `${summary.elevated_score}%` }}
              />
            </div>
          </div>

          {/* Median Score Progress Bar */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Medianvärde
              </span>
              <span className="text-sm text-gray-600">
                {summary.median_score.toFixed(1)} / 6.0
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-brand-primary h-2 rounded-full transition-all duration-500"
                style={{ width: `${(summary.median_score / 6) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* AI Exploration Progress - Only show if exploration has started */}
        {!loadingProgress && explorationProgress && explorationProgress.total_messages > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 ">
               AI-utforskning
            </h3>
            
            {/* Last explored timestamp */}
            {explorationProgress.last_explored && (
              <div className="mt-2 text-xs text-gray-500 mb-4">
                Senast utforskad: {new Date(explorationProgress.last_explored).toLocaleDateString('sv-SE', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </div>
            )}

            {/* Overall Exploration Depth */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Utforskningsdjup
                </span>
                <span className="text-sm text-gray-600">
                  {explorationProgress.exploration_depth.toFixed(0)}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-500 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${explorationProgress.exploration_depth}%` }}
                />
              </div>
            </div>

            {/* Detailed Counts */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {explorationProgress.origin_memories_count}
                </div>
                <div className="text-xs text-blue-800">
                  Ursprungsminnen
                </div>
              </div>
              
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {explorationProgress.triggers_count}
                </div>
                <div className="text-xs text-orange-800">
                  Triggers
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {explorationProgress.coping_strategies_count}
                </div>
                <div className="text-xs text-green-800">
                  Copingstrategier
                </div>
              </div>
            </div>

            {/* Session Info */}
            <div className="flex items-center justify-between text-sm text-gray-600">
              <span>
                {explorationProgress.total_messages} meddelanden • {explorationProgress.chapters_count} {explorationProgress.chapters_count === 1 ? 'session' : 'sessioner'}
              </span>
              <span>
                Fokus: <span className="font-medium capitalize">{
                  explorationProgress.current_focus === 'origin' ? 'Ursprung' :
                  explorationProgress.current_focus === 'triggers' ? 'Triggers' :
                  explorationProgress.current_focus === 'coping' ? 'Coping' :
                  'Fördjupning'
                }</span>
              </span>
            </div>

            
          </div>
        )}

        

        {/* Informational guidance based on elevation */}
        <div className={`mt-6 ${elevationAnalysis.bgColor} border ${elevationAnalysis.borderColor} rounded-lg p-6`}>
            <div className="flex items-start gap-3">
            <svg className="h-6 w-6 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
                <h4 className={`font-semibold ${elevationAnalysis.textColor} mb-1`}>
                Rekommendation
                </h4>
                <p className={`${elevationAnalysis.textColor} text-sm`}>
                {elevationAnalysis.actionable 
                    ? 'Detta livstema visar hög aktivering och kan ha betydande påverkan på ditt välbefinnande. Genom att använda vårt AI-stöd kan du få hjälp att förstå och bearbeta detta tema djupare. Dina svar sparas automatiskt.'
                    : 'Detta livstema visar låg aktivering just nu. Du kan med fördel fokusera på andra teman som visar högre aktivering. Du kan alltid återkomma till detta tema senare om du vill. Dina svar sparas automatiskt.'}
                </p>
            </div>
            </div>
        </div>

        
      </div>

      

      

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4">
        
        <button
          onClick={() => navigate('/client/life-themes')}
          className="flex-1 py-4 px-6 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          Fortsätt med andra teman
        </button>

        <button
          onClick={() => navigate('/client')}
          className="flex-1 py-4 px-6  bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:border-gray-400 hover:bg-gray-50 transition-colors"
        >
          Översikt alla teman
        </button>
      </div>

      
    </div>
  );
};

export default ThemeSummary;
